// services/ServiceProviderImageService.js - ReactJS (web)
import api from '../api/api';

class ServiceProviderImageService {
  // ==================== PROFILE IMAGE ====================

  /**
   * Upload profile image (requires auth)
   * POST /api/service-providers/:providerId/images/profile
   */
  async uploadProfileImage(providerId, file, options = {}) {
    try {
      const formData = new FormData();
      
      // Handle both browser File objects and React Native style objects
      if (file instanceof File) {
        formData.append('profileImage', file);
      } else if (file.uri) {
        formData.append('profileImage', {
          uri: file.uri,
          type: file.type || 'image/jpeg',
          name: file.name || 'profile.jpg'
        });
      } else {
        formData.append('profileImage', file);
      }

      const response = await api.post(
        `/service-providers/${providerId}/images/profile`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          params: {
            width: options.width || 800,
            height: options.height || 800
          }
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get provider profile image (public)
   * GET /api/service-providers/:providerId/images/profile
   */
  async getProviderProfileImage(providerId) {
    try {
      const response = await api.get(
        `/service-providers/${providerId}/images/profile`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove profile image (requires auth)
   * DELETE /api/service-providers/:providerId/images/profile
   */
  async removeProfileImage(providerId) {
    try {
      const response = await api.delete(
        `/service-providers/${providerId}/images/profile`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== COVER IMAGE ====================

  /**
   * Upload cover image (requires auth)
   * POST /api/service-providers/:providerId/images/cover
   */
  async uploadCoverImage(providerId, file, options = {}) {
    try {
      const formData = new FormData();
      
      // Handle both browser File objects and React Native style objects
      if (file instanceof File) {
        formData.append('coverImage', file);
      } else if (file.uri) {
        formData.append('coverImage', {
          uri: file.uri,
          type: file.type || 'image/jpeg',
          name: file.name || 'cover.jpg'
        });
      } else {
        formData.append('coverImage', file);
      }

      const response = await api.post(
        `/service-providers/${providerId}/images/cover`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          params: {
            width: options.width || 1200,
            height: options.height || 400
          }
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get provider cover image (public)
   * GET /api/service-providers/:providerId/images/cover
   */
  async getProviderCoverImage(providerId) {
    try {
      const response = await api.get(
        `/service-providers/${providerId}/images/cover`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove cover image (requires auth)
   * DELETE /api/service-providers/:providerId/images/cover
   */
  async removeCoverImage(providerId) {
    try {
      const response = await api.delete(
        `/service-providers/${providerId}/images/cover`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== IMAGES SUMMARY ====================

  /**
   * Get provider images summary (profile + cover) (public)
   * GET /api/service-providers/:providerId/images/summary
   */
  async getProviderImagesSummary(providerId) {
    try {
      const response = await api.get(
        `/service-providers/${providerId}/images/summary`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== OPTIMIZED IMAGES ====================

  /**
   * Get optimized image URL (public)
   * GET /api/service-providers/images/optimized/:publicId
   */
  async getOptimizedImageUrl(publicId, options = {}) {
    try {
      const response = await api.get(
        `/service-providers/images/optimized/${publicId}`,
        {
          params: {
            width: options.width,
            height: options.height,
            crop: options.crop || 'limit',
            quality: options.quality || 'auto'
          }
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get optimized image URL directly (helper method)
   * Returns the URL string without making an API call
   */
  getOptimizedImageUrlDirect(publicId, options = {}) {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8280/api';
    const params = new URLSearchParams();
    
    if (options.width) params.append('width', String(options.width));
    if (options.height) params.append('height', String(options.height));
    if (options.crop) params.append('crop', options.crop);
    if (options.quality) params.append('quality', options.quality);
    
    const queryString = params.toString();
    return `${baseUrl}/service-providers/images/optimized/${publicId}${queryString ? '?' + queryString : ''}`;
  }

  // ==================== DELETE ALL IMAGES ====================

  /**
   * Delete all images associated with a provider (admin only)
   * DELETE /api/service-providers/:providerId/images/all
   */
  async deleteAllProviderImages(providerId) {
    try {
      const response = await api.delete(
        `/service-providers/${providerId}/images/all`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    let errorMessage = ' ';
    
    if (error.response?.data?.data?.error?.message) {
      errorMessage = error.response.data.data.error.message;
    } else if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status,
      data: error.response?.data
    };
  }

  /**
   * Validate image file before upload
   */
  validateImageFile(file) {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp'];
    
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }
    
    if (file.size && file.size > maxSize) {
      return { valid: false, error: 'File size exceeds 10MB limit' };
    }
    
    if (file.type && !allowedTypes.includes(file.type)) {
      return { valid: false, error: `Unsupported file type: ${file.type}` };
    }
    
    return { valid: true };
  }

  /**
   * Get supported image formats
   */
  getSupportedImageFormats() {
    return ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp'];
  }

  /**
   * Get max file size in MB
   */
  getMaxFileSizeMB() {
    return 10; // 10MB
  }

  /**
   * Get image preview URL (web helper)
   */
  getImagePreviewUrl(file) {
    if (!file) return null;
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    if (file.uri) {
      return file.uri;
    }
    return null;
  }

  /**
   * Revoke image preview URL (web helper)
   */
  revokeImagePreviewUrl(url) {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Create FormData from file (web helper)
   */
  createFormData(file, fieldName = 'profileImage') {
    const formData = new FormData();
    if (file instanceof File) {
      formData.append(fieldName, file);
    } else if (file.uri) {
      formData.append(fieldName, {
        uri: file.uri,
        type: file.type || 'image/jpeg',
        name: file.name || 'image.jpg'
      });
    }
    return formData;
  }

  /**
   * Format profile image for display
   */
  formatProfileImage(image) {
    if (!image) return null;
    return {
      url: image.url,
      publicId: image.publicId,
      thumbnail: image.thumbnail,
      uploadedAt: image.uploadedAt,
    };
  }

  /**
   * Format cover image for display
   */
  formatCoverImage(image) {
    if (!image) return null;
    return {
      url: image.url,
      publicId: image.publicId,
      uploadedAt: image.uploadedAt,
    };
  }

  /**
   * Get default avatar URL
   */
  getDefaultAvatar(name) {
    if (!name) return null;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=200`;
  }

  /**
   * Get default cover image URL
   */
  getDefaultCover() {
    return null; // No default cover, return null
  }
}

export default new ServiceProviderImageService();